/* eslint-disable @typescript-eslint/no-useless-default-assignment */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAppContext } from "../../../hooks/useAppContext";
import { useLabels } from "../../../hooks/useLabels";
import { OfflineDialog } from "./offline-dialog";

const labels = {
	title: {
		label: "Label.MobileCreator.Offline.Title",
		default: "No internet connection",
	},
	requiredMessage: {
		label: "Label.MobileCreator.Offline.RequiredMessage",
		default:
			"We currently require a network request. Please fix your internet connection to continue.",
	},
	message: {
		label: "Label.MobileCreator.Offline.Message",
		default: "You are currently offline. Please check your internet connection.",
	},
	secondMessage: {
		label: "Label.MobileCreator.Offline.SecondMessage",
		default: "You are still offline. Please check your internet connection.",
	},
	close: {
		label: "Label.MobileCreator.Offline.Close",
		default: "Close",
	},
} as const;

const firstWarningDelay = 5 * 1000;
const secondWarningDelay = 30 * 1000;

/** Hook that will annoy the user that the network is offline */
const useOfflineWarning = () => {
	const { networkMonitor } = useAppContext();
	const [[offlineMessage, stillOfflineMessage]] = useLabels([
		labels.message,
		labels.secondMessage,
	]); // Labels should be prefeteched
	const [warning, setWarning] = useState(
		networkMonitor.getIsOnline() ? offlineMessage : undefined,
	);

	const toastTimeoutRef = useRef(0);

	const temporaryCloseWarning = useCallback(() => {
		setWarning(undefined);

		// Keep annoying the user with warnings
		if (!networkMonitor.getIsOnline()) {
			window.clearTimeout(toastTimeoutRef.current);
			toastTimeoutRef.current = window.setTimeout(() => {
				toastTimeoutRef.current = 0;
				// Still offline?
				if (!networkMonitor.getIsOnline()) {
					setWarning(stillOfflineMessage);
				}
			}, secondWarningDelay);
		}
	}, [networkMonitor, stillOfflineMessage]);

	useEffect(() => {
		const stopWatching = networkMonitor.watchOnline((isOnlineAndPingable) => {
			if (isOnlineAndPingable) {
				// Back Online?
				if (toastTimeoutRef.current) {
					window.clearTimeout(toastTimeoutRef.current);
					toastTimeoutRef.current = 0;
				}
				setWarning(undefined);
			} else {
				// When going offline, delay message
				window.clearTimeout(toastTimeoutRef.current);
				toastTimeoutRef.current = window.setTimeout(() => {
					toastTimeoutRef.current = 0;
					// Still offline?
					if (!networkMonitor.getIsOnline()) {
						setWarning(offlineMessage);
					}
				}, firstWarningDelay);
			}
		}).dispose;

		return () => {
			stopWatching();
			if (toastTimeoutRef.current) window.clearTimeout(toastTimeoutRef.current);
			toastTimeoutRef.current = 0;
		};
	}, [networkMonitor, offlineMessage]);

	return [warning, temporaryCloseWarning] as const;
};

export const OfflineDialogContainer = memo(function OfflineDialogContainer() {
	const { offlineEmitter } = useAppContext();
	const [isOfflineRequest, setIsOfflineRequest] = useState(false);

	const [offlineWarning, closeWarning] = useOfflineWarning();

	// Watch if offline during a network call
	useEffect(() => offlineEmitter.subscribe(setIsOfflineRequest), [offlineEmitter]);

	// Because this container is always used, it will already fetch these labels at start,
	// so translated labels normally will be available is network goes offline
	const [
		[
			title = labels.title.default,
			networkRequiredMessage = labels.requiredMessage.default,
			close = labels.close.default,
		],
		isLoadingLabels,
	] = useLabels([labels.title, labels.requiredMessage, labels.close]);

	return (isOfflineRequest || offlineWarning) && !isLoadingLabels ? (
		<OfflineDialog
			title={title}
			message={isOfflineRequest ? networkRequiredMessage : offlineWarning || ""}
			close={close}
			onClose={isOfflineRequest ? undefined : closeWarning}
		/>
	) : null;
});
